import { ReactNode, useContext, useEffect } from "react";

import { Navigate, Route, useNavigate } from "react-router-dom";

import CallHistoryPage from "@/components/connect/CallHistoryPage";
import ConnectSettingsPage from "@/components/connect/ConnectSettingsPage";
import OccurrencesPage from "@/components/connect/OccurrencesPage";
import OccurrenceView from "@/components/connect/OccurrenceView";
import { Permission } from "@/components/UserManagement/types";
import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import {
  useCommunitiesQuery,
  useServiceConfigurations,
} from "@/hooks/useCommunities";
import ProtectedPage from "@/ProtectedPage";

// Org config decides whether the agency has RubiCONNECT; the user still needs
// the app permission. Waits for the community directory so an org enabled only
// in the DB isn't bounced on first load.
const RequireConnectApp = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useContext(AuthStateContext);
  const { isDevEnv } = useDevEnvironment();
  const { isLoading } = useCommunitiesQuery();
  const serviceConfigs = useServiceConfigurations();

  if (isLoading) return null;

  const orgId = currentUser?.rubi_org_id;
  const orgApps =
    Object.values(serviceConfigs).find(
      (s) => (isDevEnv ? s.testID : s.id) === orgId,
    )?.apps ?? [];
  const { canAccessConnectApp } = userPermissionChecks(
    currentUser?.permissions?.map((p) => p.title) ?? [],
  );

  if (!canAccessConnectApp || !orgApps.includes(Permission.CONNECT)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const connectPage = (page: ReactNode) => (
  <ProtectedPage>
    <RequireConnectApp>{page}</RequireConnectApp>
  </ProtectedPage>
);

// Redirect bare /rubiconnect (and legacy /meetings) to the occurrences home.
const RubiConnectRedirectToOrgURL = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthStateContext);

  useEffect(() => {
    navigate(`/rubiconnect/${currentUser?.rubi_org_id}/occurrences`);
  }, []);
  return <></>;
};

export const rubiConnectRoutes = [
  <Route
    key="rubiconnect-occurrences"
    path="rubiconnect/:orgId/occurrences"
    element={connectPage(<OccurrencesPage />)}
  />,
  <Route
    key="rubiconnect-occurrence"
    path="rubiconnect/:orgId/occurrences/:occurrenceId"
    element={connectPage(<OccurrenceView />)}
  />,
  <Route
    key="rubiconnect-history"
    path="rubiconnect/:orgId/history"
    element={connectPage(<CallHistoryPage />)}
  />,
  <Route
    key="rubiconnect-settings"
    path="rubiconnect/:orgId/settings"
    element={connectPage(<ConnectSettingsPage />)}
  />,
  <Route
    key="rubiconnect-redirect"
    path="rubiconnect/*"
    element={connectPage(<RubiConnectRedirectToOrgURL />)}
  />,
];
